import { Box } from '@deckee/deck-hand';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectUnitsConfig } from '../../../../../../services/store/modules/app-controls';
import { formatTemperature } from '../../../../../helpers/data-helpers';
import { TemperatureChart } from '../../charts';
import {
  enrichedSeriesToTimeSeries,
  formatTemperatureHeader,
  getClosestEnrichedPoint,
  getFeelsLikeTemperatureForChartSelection,
  getForecastDayDomain,
  graphPointsToEnrichedSeries,
  type WeatherForecastGraphPoint,
} from '../../../helpers/graph/graphData';
import { type WeatherForecastDaySelectHandler } from '../../../helpers/analytics/weatherForecastAnalytics';
import { splitForecastDaysForOutlook } from '../../../helpers/forecastHorizon';
import { type WeatherForecastDay } from '../../../helpers/outlook/formatDays';
import { useGraphTime } from '../GraphTimeContext';
import CollapsibleCard from '../../CollapsibleCard/CollapsibleCard';
import CollapsibleSection from '../../CollapsibleSection/CollapsibleSection';
import GraphCardHeader from '../GraphCardHeader';
import Outlook, {
  getOutlookTemperatureScale,
  type OutlookDay,
} from '../../Outlook/Outlook';
import GraphCardNoData from '../GraphCardNoData';

export interface TemperatureCardProps {
  temperaturePoints: WeatherForecastGraphPoint[];
  selectedDate: string;
  timeZone: string;
  isToday: boolean;
  latitude: number;
  longitude: number;
  forecastDays?: WeatherForecastDay[];
  onSelectDay?: WeatherForecastDaySelectHandler;
}

const TemperatureCard: FC<TemperatureCardProps> = ({
  temperaturePoints,
  selectedDate,
  timeZone,
  latitude,
  longitude,
  forecastDays = [],
  onSelectDay,
}) => {
  const unitsConfig = useSelector(selectUnitsConfig);
  const isFahrenheit = unitsConfig?.temperature === 'fahrenheit';

  const outlookDays = useMemo<OutlookDay[]>(
    () =>
      forecastDays.map((day) => ({
        date: day.date,
        day: day.dayLabel,
        isWeekend: day.isWeekend,
        weatherIcon: day.weatherIcon,
        minimumTemperature: formatTemperature(
          day.minimumTemperature,
          unitsConfig,
          true,
          true,
        ),
        maximumTemperature: formatTemperature(
          day.maximumTemperature,
          unitsConfig,
          true,
          true,
        ),
      })),
    [forecastDays, unitsConfig],
  );
  const {
    nearTermDays: nearTermOutlookDays,
    extendedDays: extendedOutlookDays,
  } = splitForecastDaysForOutlook(outlookDays);
  const outlookScale = useMemo(
    () => getOutlookTemperatureScale(outlookDays),
    [outlookDays],
  );

  const {
    selectedChartTime,
    defaultChartTime,
    handleSelectedChartTimeChange,
    handleChartScrubStart,
    handleChartScrubEnd,
  } = useGraphTime('temperature');

  const temperatureSeries = useMemo(
    () =>
      graphPointsToEnrichedSeries(temperaturePoints, selectedDate, timeZone),
    [selectedDate, temperaturePoints, timeZone],
  );

  const domain = useMemo(
    () => getForecastDayDomain(selectedDate, timeZone),
    [selectedDate, timeZone],
  );

  const chartData = useMemo(
    () => enrichedSeriesToTimeSeries(temperatureSeries, domain),
    [domain, temperatureSeries],
  );

  const selectedTemperaturePoint = useMemo(() => {
    if (!selectedChartTime) {
      return undefined;
    }

    return getClosestEnrichedPoint(temperatureSeries, selectedChartTime);
  }, [selectedChartTime, temperatureSeries]);

  const primaryDisplay = formatTemperatureHeader(
    selectedTemperaturePoint?.value,
    unitsConfig,
  );

  const feelsLikeDisplay = useMemo(() => {
    const feelsLikeTemperatureForSelection =
      getFeelsLikeTemperatureForChartSelection(selectedTemperaturePoint);

    if (feelsLikeTemperatureForSelection == null) {
      return null;
    }

    const formatted = formatTemperature(
      feelsLikeTemperatureForSelection,
      unitsConfig,
      true,
      true,
    );

    return formatted != null ? `Feels like ${formatted}°` : null;
  }, [selectedTemperaturePoint, unitsConfig]);

  if (chartData.length === 0) {
    return (
      <CollapsibleCard
        title="Temperature"
        testId="weather-forecast-temperature-card"
        analyticsCard="temperature"
      >
        <GraphCardNoData
          chartLabel="Temperature"
          stationType="general"
          isLocationMissingData={temperaturePoints.length === 0}
          latitude={latitude}
          longitude={longitude}
          selectedDate={selectedDate}
        />
      </CollapsibleCard>
    );
  }

  return (
    <CollapsibleCard
      title="Temperature"
      testId="weather-forecast-temperature-card"
      analyticsCard="temperature"
    >
      <GraphCardHeader primary={primaryDisplay} secondary={feelsLikeDisplay} />

      <Box width="100%">
        <TemperatureChart
          data={chartData}
          domain={domain}
          timeZone={timeZone}
          temperatureSeries={temperatureSeries}
          selectedTime={selectedChartTime}
          defaultSelectedTime={defaultChartTime}
          onSelectedTimeChange={handleSelectedChartTimeChange}
          onScrubStart={handleChartScrubStart}
          onScrubEnd={handleChartScrubEnd}
        />
      </Box>

      {nearTermOutlookDays.length > 0 && (
        <CollapsibleSection
          title="7 day outlook"
          testId="weather-forecast-7-day-outlook"
          analyticsCard="7 day outlook"
        >
          <Outlook
            forecastDays={nearTermOutlookDays}
            isFahrenheit={isFahrenheit}
            selectedDate={selectedDate}
            onSelectDay={onSelectDay}
            daySelectionSource="7 day outlook"
            scaleMin={outlookScale.min}
            scaleMax={outlookScale.max}
          />
        </CollapsibleSection>
      )}

      {extendedOutlookDays.length > 0 && (
        <CollapsibleSection
          title="8-15 day outlook"
          testId="weather-forecast-8-15-day-outlook"
          analyticsCard="8-15 day outlook"
          defaultExpanded={false}
        >
          <Outlook
            forecastDays={extendedOutlookDays}
            isFahrenheit={isFahrenheit}
            selectedDate={selectedDate}
            onSelectDay={onSelectDay}
            daySelectionSource="8-15 day outlook"
            scaleMin={outlookScale.min}
            scaleMax={outlookScale.max}
          />
        </CollapsibleSection>
      )}
    </CollapsibleCard>
  );
};

export default TemperatureCard;
